import type { DateRange } from './types';
import type { PaymentChannel } from './paymentMethods';

export type CashierPeriodPreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type CashierTransactionKind = 'payment' | 'refund';
export type CashierTransactionStatus = 'received' | 'refunded';
export type CashierPeriodGrain = 'hour' | 'day' | 'week' | 'month';

export interface CashierFilterState {
  period: CashierPeriodPreset;
  customFrom: string | null;
  customTo: string | null;
  paymentMethods: string[];
  channels: PaymentChannel[];
  practitionerIds: string[];
  locationIds: string[];
  statuses: CashierTransactionStatus[];
  search: string;
}

export const EMPTY_CASHIER_FILTERS: CashierFilterState = {
  period: 'this_month',
  customFrom: null,
  customTo: null,
  paymentMethods: [],
  channels: [],
  practitionerIds: [],
  locationIds: [],
  statuses: [],
  search: '',
};

export interface CashierPaymentRow {
  id: string;
  payment_date: string;
  /** When the payment row was recorded. Manual dates are often noon; this is the real clock. */
  recorded_at?: string | null;
  amount_cents: number;
  currency: string;
  payment_method_raw: string | null;
  payment_method_key: string;
  channel: PaymentChannel;
  payment_reference: string | null;
  notes: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
  patient_id: string | null;
  patient_name: string | null;
  practitioner_id: string | null;
  practitioner_name: string | null;
  related_practitioner_ids: string[];
  clinic_id: string;
  clinic_name: string | null;
  kind: CashierTransactionKind;
  status: CashierTransactionStatus;
}

export interface CashierSummary {
  total_received_cents: number;
  refunds_cents: number;
  net_received_cents: number;
  transaction_count: number;
  refund_count: number;
  /** Remaining invoice balance. Not a payment-ledger amount. */
  outstanding_cents: number;
  outstanding_count: number;
}

export type CashierOutstandingStatus = 'issued' | 'partial' | 'overdue';

export interface CashierOutstandingInvoice {
  id: string;
  invoice_number: string | null;
  patient_id: string | null;
  patient_name: string | null;
  issued_at: string | null;
  due_at: string | null;
  total_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  status: CashierOutstandingStatus | string;
  clinic_id: string;
  clinic_name: string | null;
  related_practitioner_ids: string[];
}

export interface CashierMethodBreakdown {
  method_key: string;
  received_cents: number;
  refund_cents: number;
  net_cents: number;
  transaction_count: number;
  refund_count: number;
}

export interface CashierCashSection {
  received_cents: number;
  refund_cents: number;
  net_cents: number;
  transaction_count: number;
  refund_count: number;
}

export interface CashierClinioPaySection {
  received_cents: number;
  refund_cents: number;
  net_cents: number;
  transaction_count: number;
  refund_count: number;
  /** Null when Clinio Pay does not persist fee data on invoice_payments. */
  fees_cents: number | null;
  /** Null when payout/settlement is not stored locally. */
  payout_received_cents: number | null;
  expected_payout_cents: number | null;
}

export interface CashierPeriodRow {
  bucket: string;
  label: string;
  received_cents: number;
  refund_cents: number;
  net_cents: number;
  transaction_count: number;
  refund_count: number;
}

export interface CashierReportResult {
  titleKey: string;
  period: DateRange;
  grain: CashierPeriodGrain;
  timezone: string;
  currency: string;
  clinic_name: string | null;
  summary: CashierSummary;
  methods: CashierMethodBreakdown[];
  refunds: CashierMethodBreakdown[];
  cash: CashierCashSection;
  clinioPay: CashierClinioPaySection;
  periodRows: CashierPeriodRow[];
  transactions: CashierPaymentRow[];
  transactionsTruncated: boolean;
  transactionTotalCount: number;
  outstandingInvoices: CashierOutstandingInvoice[];
  availableMethods: string[];
  empty: boolean;
  servicedRows?: CashierServicedRow[];
  creditNotes?: CashierCreditNoteRow[];
  reconciliation?: CashierReconciliation | null;
}

/** A service completed in the report period. */
export interface CashierServicedRow {
  id: string;
  invoiceId: string | null;
  invoiceNumber: string | null;
  patientName: string | null;
  practitionerName: string | null;
  serviceName: string | null;
  /** Amount incl. VAT. A klip visit shows the list price, not the 0 kr invoice total. */
  amountCents: number;
  clipCents: number;
}

/** A credit note issued in the report period. Amount is stored positive and shown negative. */
export interface CashierCreditNoteRow {
  id: string;
  invoiceNumber: string | null;
  originalInvoiceId: string | null;
  originalInvoiceNumber: string | null;
  patientName: string | null;
  amountCents: number;
  issuedAt: string | null;
}

/**
 * Period services only.
 * behandlet − krediteret = betalt − refunderet + klip + udestående.
 */
export interface CashierReconciliation {
  servicedCents: number;
  creditedCents: number;
  paidCents: number;
  refundedCents: number;
  clipCents: number;
  outstandingCents: number;
}

export function emptyCashierSummary(): CashierSummary {
  return {
    total_received_cents: 0,
    refunds_cents: 0,
    net_received_cents: 0,
    transaction_count: 0,
    refund_count: 0,
    outstanding_cents: 0,
    outstanding_count: 0,
  };
}
